/**
 * Режим «Карта» беты (ISSUE-164): регион игрока, атака участка, доход, энергия. Только с `X-Game-Mode: beta`.
 * Причины отказа — в `ApiError.message` токеном вида `map: <reason>` (см. `docs/context/beta-map.md`).
 */
import { z } from "zod";
import { ChestType } from "../../balance/beta";
import { AttackDefense, AttackLog } from "../../map";
import { HeroId, PosInt, UInt, UnixMs } from "../../common";
import { RunState, WaveResult } from "../../wave-log";
import type { EndpointDef } from "../endpoint";
import { Empty } from "../errors";
import { PlayerState } from "../player";

/** Энергия атак на момент `serverTime`. */
export const MapEnergy = z.object({
  energy: UInt,
  max: PosInt,
  /** Когда придёт следующая единица (мс Unix); `null` — энергия полная. */
  nextAt: UnixMs.nullable(),
  /** Цена одной единицы в кристаллах. */
  gemsPerUnit: PosInt,
});
export type MapEnergy = z.infer<typeof MapEnergy>;

/** Доход: монеты, кристаллы, осколки (число; герой выбирается при выдаче). */
export const MapIncome = z.object({ coins: UInt, gems: UInt, shards: UInt });
export type MapIncome = z.infer<typeof MapIncome>;

/** Вид защиты участка для панели: зомби или копии героев владельца. */
export const MapPlotDefense = z.object({
  kind: z.enum(["zombies", "heroes"]),
  /** Прочность базы участка. */
  baseHp: PosInt,
  /** Героев в защите (для зомби — 0). */
  heroes: UInt,
});
export type MapPlotDefense = z.infer<typeof MapPlotDefense>;

export const MapPlot = z.object({
  x: z.number().int().min(0),
  y: z.number().int().min(0),
  /** Сила участка 1…N: растёт от края карты к центру. */
  level: PosInt,
  /** Владелец; `null` — свободный участок с зомби. */
  owner: z
    .object({
      /** Номер цвета (0…capacity−1): клиент красит участок по нему. */
      color: UInt,
      name: z.string(),
      mine: z.boolean(),
    })
    .nullable(),
  /** Щит до (мс Unix); `null` — нет щита. Атаковать под щитом нельзя. */
  shieldUntil: UnixMs.nullable(),
  defense: MapPlotDefense,
  /** Доход участка в час. */
  perHour: MapIncome,
  /** Очки рейтинга за участок. */
  rating: PosInt,
  /** Накопленный доход — только у своих участков. */
  income: MapIncome.extend({ full: z.boolean() }).nullable(),
});
export type MapPlot = z.infer<typeof MapPlot>;

export const MapRankRow = z.object({
  rank: PosInt,
  name: z.string(),
  color: UInt,
  rating: UInt,
  plots: UInt,
  mine: z.boolean(),
});
export type MapRankRow = z.infer<typeof MapRankRow>;

/** Регион игрока целиком: сетка, энергия, рейтинг. */
export const MapView = z.object({
  region: z.object({ id: PosInt, tier: PosInt, grid: PosInt, players: PosInt, capacity: PosInt }),
  energy: MapEnergy,
  /** 100 участков (grid × grid), по рядам: y, затем x. */
  plots: z.array(MapPlot),
  /** Рейтинг региона: топ-10 и строка игрока (если не в топе). */
  ranking: z.array(MapRankRow),
  me: z.object({
    rank: PosInt,
    rating: UInt,
    plots: UInt,
    /** Накоплено по всем своим участкам — для кнопки «Забрать всё». */
    income: MapIncome,
    /** Сундук за первый захват уже получен. */
    firstCaptureDone: z.boolean(),
  }),
  /** Параметры боя атаки (для подсказок): лимит времени, щит после захвата, потолок накопления. */
  rules: z.object({ timeLimitTicks: PosInt, shieldMs: PosInt, incomeCapHours: PosInt }),
  serverTime: UnixMs,
});
export type MapView = z.infer<typeof MapView>;

/** Старт атаки участка. Тратит 1 энергию. Сервер выдаёт сид, состав защиты и состояние забежки атакующего. */
export const MapAttackStartRequest = z.object({ x: z.number().int().min(0), y: z.number().int().min(0) });
export const MapAttackStartResponse = z.object({
  attackId: z.uuid(),
  x: UInt,
  y: UInt,
  /** Что защищает участок: зомби или копии героев владельца. */
  kind: z.enum(["zombies", "heroes"]),
  seed: z
    .number()
    .int()
    .min(0)
    .max(2 ** 32 - 1),
  /** Состав защиты и забежка атакующего — именно с ними клиент создаёт бой (`createBattle({ run: start, seed, attack: defense })`). */
  defense: AttackDefense,
  start: RunState,
  balanceVersion: z.string(),
  /** Не позже этого времени нужно прислать журнал (мс Unix). */
  expiresAt: UnixMs,
  energy: MapEnergy,
});

/** Итог атаки. `captured` — участок твой; `defeat` — бой проигран; `taken` — победа, но участок успели захватить раньше. */
export const MapAttackOutcome = z.enum(["captured", "defeat", "taken"]);
export type MapAttackOutcome = z.infer<typeof MapAttackOutcome>;

export const MapAttackFinishRequest = z.object({ log: AttackLog });
export const MapAttackFinishResponse = z.object({
  outcome: MapAttackOutcome,
  /** Итог боя, как его пересчитал сервер (прав сервер). */
  result: WaveResult,
  /** Результат клиента не совпал с серверным. */
  corrected: z.boolean(),
  /** Награда за бой: монеты (при `taken` — частичная) и сундук за первый захват. */
  reward: z.object({ coins: UInt, chest: ChestType.nullable() }),
  /** Регион после боя (участок перекрашен). */
  view: MapView,
  state: PlayerState,
});

/** «Забрать доход»: с одного участка (`x`,`y`) или со всех своих (без полей). */
export const MapClaimRequest = z
  .object({ x: z.number().int().min(0).optional(), y: z.number().int().min(0).optional() })
  .refine((r) => (r.x === undefined) === (r.y === undefined), "x and y together");
export const MapClaimResponse = z.object({
  claimed: MapIncome,
  /** Осколки по героям (кого выбрал сервер). */
  shards: z.partialRecord(HeroId, UInt),
  view: MapView,
  state: PlayerState,
});

/** Докупка энергии за кристаллы (`count` единиц, до максимума). Заголовок `Idempotency-Key`. */
export const MapEnergyBuyRequest = z.object({ count: PosInt.default(1) });
export const MapEnergyBuyResponse = z.object({ spent: UInt, energy: MapEnergy, state: PlayerState });

export const mapEndpoints = {
  betaMapGet: {
    method: "GET",
    path: "/api/beta/map",
    request: Empty,
    response: MapView,
    auth: true,
  },
  betaMapAttackStart: {
    method: "POST",
    path: "/api/beta/map/attack/start",
    request: MapAttackStartRequest,
    response: MapAttackStartResponse,
    auth: true,
  },
  betaMapAttackFinish: {
    method: "POST",
    path: "/api/beta/map/attack/finish",
    request: MapAttackFinishRequest,
    response: MapAttackFinishResponse,
    auth: true,
  },
  betaMapClaim: {
    method: "POST",
    path: "/api/beta/map/claim",
    request: MapClaimRequest,
    response: MapClaimResponse,
    auth: true,
  },
  betaMapEnergyBuy: {
    method: "POST",
    path: "/api/beta/map/energy/buy",
    request: MapEnergyBuyRequest,
    response: MapEnergyBuyResponse,
    auth: true,
  },
} as const satisfies Record<string, EndpointDef>;

/** Токены причин отказа (`ApiError.message` = `map: <reason>`). */
export const MAP_REASONS = [
  "no_energy",
  "shield",
  "own_plot",
  "no_plot",
  "no_heroes",
  "expired",
  "not_started",
  "energy_full",
  "version",
] as const;
